/**
 * Read-only client detail view model.
 * mergeClientSystems is the homeowner dashboard merge (ClientDashboardV3):
 * a client_systems row with any system_data replaces that category's legacy
 * client columns; an empty system_data object falls through to those columns.
 * The detail sections still list the client columns the agent form stores,
 * and fill any column the system row does not set.
 */

import { FEATURE_LABELS, normalizeFeatureList, PROPERTY_TYPE_LABELS } from './homeownerIntake';
import { constructionLabel, exteriorLabel, foundationLabel, heaterSummary, roofMaterialLabel } from './homeProfile';
import { countyLabel } from './serviceArea';
import { formatPhoneDisplay, isPlaceholderPhone } from './phoneFormat';

const NOT_PROVIDED = 'Not provided yet';

const SYSTEM_CATEGORIES = [
  { id: 'hvac', name: 'HVAC System', fields: ['manufacturer', 'model_number', 'serial_number', 'install_year', 'capacity', 'type'] },
  { id: 'roof', name: 'Roof', fields: ['type', 'install_year', 'manufacturer', 'warranty_years'] },
  { id: 'water_heater', name: 'Water Heater', fields: ['manufacturer', 'model_number', 'serial_number', 'install_year', 'capacity', 'type'] },
  { id: 'electrical_panel', name: 'Electrical Panel', fields: ['manufacturer', 'model_number', 'amp_rating', 'install_year', 'main_breaker_size'] },
  { id: 'plumbing', name: 'Main Plumbing', fields: ['material', 'install_year', 'main_line_size', 'last_inspection'] },
  { id: 'well_pump', name: 'Well Pump', fields: ['manufacturer', 'model_number', 'install_year', 'gpm_rating', 'depth', 'tank_size'] },
  { id: 'water_filter', name: 'Water Filter/Softener', fields: ['manufacturer', 'model_number', 'install_year', 'type', 'last_service'] },
  { id: 'generator', name: 'Whole Home Generator', fields: ['manufacturer', 'model_number', 'serial_number', 'install_year', 'kw_rating', 'fuel_type'] },
  { id: 'pool_equipment', name: 'Pool Equipment', fields: ['pump_model', 'pump_year', 'heater_model', 'heater_year', 'filter_model', 'filter_year'] },
  { id: 'solar_panels', name: 'Solar Panels', fields: ['manufacturer', 'model_number', 'install_year', 'kw_rating', 'panel_count', 'inverter_model'] },
  { id: 'septic_system', name: 'Septic System', fields: ['tank_size', 'install_year', 'last_pumping', 'type', 'drainfield_year'] },
  { id: 'washer_dryer', name: 'Washer & Dryer', fields: ['washer_manufacturer', 'washer_model', 'washer_year', 'dryer_manufacturer', 'dryer_model', 'dryer_year'] },
  { id: 'refrigerator', name: 'Refrigerator', fields: ['manufacturer', 'model_number', 'serial_number', 'purchase_year'] },
  { id: 'dishwasher', name: 'Dishwasher', fields: ['manufacturer', 'model_number', 'serial_number', 'install_year'] },
  { id: 'oven_range', name: 'Oven/Range', fields: ['manufacturer', 'model_number', 'serial_number', 'install_year', 'fuel_type'] },
  { id: 'microwave', name: 'Microwave', fields: ['manufacturer', 'model_number', 'install_year'] },
];


const INVITE_STATUS_LABELS = {
  not_sent: 'Not sent',
  sent: 'Sent',
  failed: 'Failed',
  suppressed: "Can't email",
  accepted: 'Accepted',
};

const SOURCE_LABELS = {
  manual: 'Manual',
  csv: 'CSV',
  api: 'API',
  legacy: 'Legacy',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CLIENT_SYSTEM_SECTIONS = [
  {
    id: 'hvac',
    name: 'HVAC',
    systemId: 'hvac',
    fields: [
      { label: 'Count', clientKey: 'hvac_count', dataKeys: ['count'] },
      { label: 'Types', clientKey: 'hvac_types', dataKeys: ['types', 'type'] },
      { label: 'Years', clientKey: 'hvac_install_years', dataKeys: ['install_years', 'install_year'] },
    ],
  },
  {
    id: 'roof',
    name: 'Roof',
    systemId: 'roof',
    fields: [
      { label: 'Type', clientKey: 'roof_type', dataKeys: ['type'] },
      { label: 'Material', clientKey: 'roof_material', dataKeys: ['material', 'roof_material'] },
      { label: 'Year', clientKey: 'roof_install_year', dataKeys: ['install_year'] },
    ],
  },
  {
    id: 'water_heater',
    name: 'Water heater',
    systemId: 'water_heater',
    fields: [
      { label: 'Type', clientKey: 'water_heater_type', dataKeys: ['type'] },
      { label: 'Year', clientKey: 'water_heater_install_year', dataKeys: ['install_year'] },
    ],
  },
  {
    id: 'water',
    name: 'Water',
    systemId: 'well_pump',
    fields: [
      { label: 'Source', clientKey: 'water_source', dataKeys: ['water_source'] },
      { label: 'Well pump year', clientKey: 'well_pump_install_year', dataKeys: ['install_year'] },
    ],
  },
  {
    id: 'sewer',
    name: 'Sewer',
    systemId: 'septic_system',
    fields: [
      { label: 'Type', clientKey: 'sewer_type', dataKeys: ['sewer_type'] },
      { label: 'Septic year', clientKey: 'septic_install_year', dataKeys: ['install_year'] },
    ],
  },
  {
    id: 'pool',
    name: 'Pool',
    systemId: 'pool_equipment',
    fields: [
      { label: 'Pump year', clientKey: 'pool_pump_install_year', dataKeys: ['pump_year'] },
      { label: 'Heater year', clientKey: 'pool_heater_install_year', dataKeys: ['heater_year'] },
    ],
  },
];

function raw(value) {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map((item) => raw(item)).filter(Boolean).join(', ');
  }
  return String(value).trim();
}

function shown(value) {
  const text = raw(value);
  return text || NOT_PROVIDED;
}

function shownPhone(value) {
  const text = raw(value);
  if (!text || isPlaceholderPhone(text)) return NOT_PROVIDED;
  return formatPhoneDisplay(text) || text;
}

function labeled(map, value) {
  const text = raw(value);
  if (!text) return NOT_PROVIDED;
  return map[text] || text;
}

function titleCase(value) {
  const text = raw(value);
  if (!text) return NOT_PROVIDED;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function fieldLabel(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function dateKeyFromDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shownDate(value) {
  if (value == null || raw(value) === '') return NOT_PROVIDED;
  if (value instanceof Date || /T/.test(String(value))) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return NOT_PROVIDED;
    return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw(value));
  if (!match) return shown(value);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return shown(value);
  return `${MONTHS[month - 1]} ${day}, ${match[1]}`;
}

function todayKey(now) {
  const date = now instanceof Date ? now : new Date(now || Date.now());
  if (Number.isNaN(date.getTime())) return dateKeyFromDate(new Date());
  return dateKeyFromDate(date);
}

function presentValue(value) {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.some((item) => item != null && item !== '');
  return true;
}

function legacySystemPresent(client, systemType) {
  const row = client || {};
  switch (systemType) {
    case 'hvac':
      return Boolean(row.hvac_install_years && row.hvac_install_years.some((year) => year !== null))
        || presentValue(row.hvac_types);
    case 'roof':
      return presentValue(row.roof_install_year)
        || presentValue(row.roof_year)
        || presentValue(row.roof_material)
        || presentValue(row.roof_type);
    case 'water_heater':
      return presentValue(row.water_heater_install_year) || presentValue(row.water_heater_type);
    case 'well_pump':
      return presentValue(row.well_pump_install_year);
    case 'septic_system':
      return presentValue(row.septic_install_year);
    case 'pool_equipment':
      return presentValue(row.pool_pump_install_year) || presentValue(row.pool_heater_install_year);
    default:
      return false;
  }
}

function legacySystemData(client, systemType) {
  const row = client || {};
  switch (systemType) {
    case 'hvac':
      return {
        install_year: row.hvac_install_years && row.hvac_install_years[0],
        type: row.hvac_types && row.hvac_types[0],
        count: row.hvac_count,
      };
    case 'roof':
      return {
        install_year: row.roof_install_year || row.roof_year,
        type: row.roof_type,
        material: row.roof_material,
      };
    case 'water_heater':
      return {
        install_year: row.water_heater_install_year,
        type: row.water_heater_type,
      };
    case 'well_pump':
      return {
        install_year: row.well_pump_install_year,
      };
    case 'septic_system':
      return {
        install_year: row.septic_install_year,
      };
    case 'pool_equipment':
      return {
        pump_year: row.pool_pump_install_year,
        heater_year: row.pool_heater_install_year,
      };
    default:
      return {};
  }
}

function mergeClientSystems(client, systems) {
  const rows = Array.isArray(systems) ? systems : [];
  const merged = [];
  SYSTEM_CATEGORIES.forEach((category) => {
    const dbSystem = rows.find((system) => system && system.system_type === category.id);
    if (dbSystem && dbSystem.system_data && Object.keys(dbSystem.system_data).length > 0) {
      merged.push({
        id: category.id,
        name: category.name,
        data: dbSystem.system_data,
        source: 'onboarding',
        hasData: true,
      });
      return;
    }
    if (legacySystemPresent(client, category.id)) {
      merged.push({
        id: category.id,
        name: category.name,
        data: legacySystemData(client, category.id),
        source: 'legacy',
        hasData: true,
      });
    }
  });
  return merged;
}

function systemDataFor(merged, systemId) {
  const row = merged.find((system) => system.id === systemId && system.source === 'onboarding');
  return row ? row.data || {} : null;
}

function firstSystemValue(data, keys) {
  if (!data) return undefined;
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (Object.prototype.hasOwnProperty.call(data, key) && raw(data[key])) return data[key];
  }
  return undefined;
}

function displayColumn(spec, value) {
  if (spec.clientKey === 'roof_material') return roofMaterialLabel(value) || shown(value);
  if (spec.clientKey === 'water_heater_type') return heaterSummary(value) || shown(value);
  return shown(value);
}

function systemDetailSections(client, systems) {
  const row = client || {};
  const merged = mergeClientSystems(row, systems);
  const named = new Set(CLIENT_SYSTEM_SECTIONS.map((section) => section.systemId));
  const sections = CLIENT_SYSTEM_SECTIONS.map((section) => {
    const data = systemDataFor(merged, section.systemId) || {};
    const usingSystem = systemDataFor(merged, section.systemId) != null;
    const used = new Set();
    const fields = section.fields.map((spec) => {
      spec.dataKeys.forEach((key) => used.add(key));
      const fromSystem = usingSystem ? firstSystemValue(data, spec.dataKeys) : undefined;
      const value = fromSystem !== undefined ? fromSystem : row[spec.clientKey];
      return { label: spec.label, value: displayColumn(spec, value) };
    });
    if (usingSystem) {
      Object.keys(data).forEach((key) => {
        if (used.has(key) || !raw(data[key])) return;
        fields.push({ label: fieldLabel(key), value: shown(data[key]) });
      });
    }
    return { id: section.id, name: section.name, fields };
  });

  merged.forEach((system) => {
    if (named.has(system.id)) return;
    const fields = Object.keys(system.data || {})
      .filter((key) => raw(system.data[key]))
      .map((key) => ({ label: fieldLabel(key), value: shown(system.data[key]) }));
    if (fields.length > 0) sections.push({ id: system.id, name: system.name, fields });
  });
  return sections;
}

function fullAddress(client) {
  const row = client || {};
  const cityState = [raw(row.city), raw(row.state)].filter(Boolean).join(', ');
  const cityLine = [cityState, raw(row.zip_code)].filter(Boolean).join(' ');
  const full = [raw(row.address), cityLine].filter(Boolean).join(', ');
  return full || NOT_PROVIDED;
}

/** One line for a client card: the address when any part exists, otherwise the type. */
function clientCardSubtitle(client) {
  const row = client || {};
  const address = fullAddress(row);
  if (address !== NOT_PROVIDED) return address;
  const type = raw(row.property_type);
  return (type && PROPERTY_TYPE_LABELS[type]) || '';
}

function hasPasswordLabel(client) {
  const row = client || {};
  if (row.has_password === true) return 'Yes';
  if (row.has_password === false) return 'No';
  if (typeof row.password_hash === 'string' && /^[$]2[aby][$]/.test(row.password_hash)) return 'Yes';
  if (row.password_hash == null && row.has_password == null) return NOT_PROVIDED;
  return 'No';
}

function presentFeatureLabels(features) {
  const normalized = normalizeFeatureList(features);
  return normalized.features.map((id) => FEATURE_LABELS[id]).filter(Boolean);
}

function taskCounts(tasks, now = new Date()) {
  const today = todayKey(now);
  const counts = { pending: 0, overdue: 0, completed: 0 };
  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    if (!task) return;
    if (task.completed) {
      counts.completed += 1;
      return;
    }
    const due = /^(\d{4}-\d{2}-\d{2})/.exec(raw(task.due_date));
    if (due && due[1] < today) counts.overdue += 1;
    else counts.pending += 1;
  });
  return counts;
}

function documentRows(documents, clientId) {
  const seen = new Set();
  const rows = [];
  (Array.isArray(documents) ? documents : []).forEach((doc) => {
    if (!doc) return;
    if (clientId && doc.client_id && doc.client_id !== clientId) return;
    if (doc.id && seen.has(doc.id)) return;
    if (doc.id) seen.add(doc.id);
    rows.push({
      id: doc.id,
      title: shown(doc.title || doc.file_name),
      category: shown(doc.category),
    });
  });
  return rows;
}

function clientDetailModel(client, extras = {}) {
  const row = client || {};
  const name = [raw(row.first_name), raw(row.last_name)].filter(Boolean).join(' ');
  const documents = documentRows(extras.documents, extras.clientId || row.id);
  return {
    header: [
      { label: 'Name', value: shown(name) },
      { label: 'Email', value: shown(row.email) },
      { label: 'Phone', value: shownPhone(row.phone) },
      { label: 'Status', value: titleCase(row.status) },
      { label: 'Invite status', value: labeled(INVITE_STATUS_LABELS, row.invite_status) },
      { label: 'Invite code', value: shown(row.invite_code) },
      { label: 'Has password', value: hasPasswordLabel(row) },
      { label: 'Source', value: labeled(SOURCE_LABELS, row.source) },
      { label: 'Date added', value: shownDate(row.date_added) },
      { label: 'Last active', value: shownDate(row.last_active) },
    ],
    property: [
      { label: 'Address', value: fullAddress(row) },
      { label: 'County', value: countyLabel(row.property_county_fips) || NOT_PROVIDED },
      { label: 'Type', value: labeled(PROPERTY_TYPE_LABELS, row.property_type) },
      { label: 'Year built', value: shown(row.year_built) },
      { label: 'Sq ft', value: shown(row.square_feet) },
      { label: 'Lot size', value: shown(row.lot_size) },
      { label: 'Construction', value: constructionLabel(row.construction_type) || NOT_PROVIDED },
      { label: 'Exterior', value: exteriorLabel(row.exterior_finish) || NOT_PROVIDED },
      { label: 'Foundation', value: foundationLabel(row.foundation_type) || NOT_PROVIDED },
      { label: 'Roof', value: roofMaterialLabel(row.roof_material) || NOT_PROVIDED },
      { label: 'HVAC count', value: shown(row.hvac_count) },
    ],
    photoUrl: raw(row.property_photo_url) || null,
    systems: systemDetailSections(row, extras.systems),
    features: presentFeatureLabels(row.features),
    tasks: taskCounts(extras.tasks, extras.now),
    documents,
    plan: planAnswerFields(row),
  };
}

const FILTER_LABELS = {
  '1in': 'Thin 1 inch',
  '4in': 'Thick 4–5 inch',
  washable: 'Washable',
  unknown: 'Not sure',
};

function planAnswerFields(client) {
  const row = client || {};
  const filter = FILTER_LABELS[String(row.filter_type || '').toLowerCase()];
  return [
    { label: 'Insurance renewal', value: shownDate(row.insurance_renewal_date) },
    { label: 'Insurance term', value: row.insurance_term_months ? `${row.insurance_term_months} months` : NOT_PROVIDED },
    { label: 'Flood renewal', value: shownDate(row.flood_renewal_date) },
    { label: 'Flood term', value: row.flood_term_months ? `${row.flood_term_months} months` : NOT_PROVIDED },
    { label: 'Warranty renewal', value: shownDate(row.warranty_renewal_date) },
    { label: 'Warranty term', value: row.warranty_term_months ? `${row.warranty_term_months} months` : NOT_PROVIDED },
    { label: 'Filter', value: filter || NOT_PROVIDED },
    { label: 'Roof year', value: shown(row.roof_year || row.roof_install_year) },
    { label: 'AC year', value: shown(Array.isArray(row.hvac_install_years) ? row.hvac_install_years[0] : row.ac_year) },
  ];
}

export {
  NOT_PROVIDED,
  SYSTEM_CATEGORIES,
  mergeClientSystems,
  systemDetailSections,
  presentFeatureLabels,
  taskCounts,
  documentRows,
  clientDetailModel,
  planAnswerFields,
  clientCardSubtitle,
};
